/**
 * Presentational sections for `/admin/automation/slots` (A7Y-3600) — the live
 * state of every UKKO runner slot. Every component here is pure: `SlotsPage`
 * owns the fetch, this file only renders what it is given. State derivation
 * (which chip, which label, which group) lives in `slots-display.ts` — this
 * file renders, it does not decide (§3/F3).
 *
 * `BannerRule` is imported from `WorkloadsSections.tsx` rather than copied a
 * third time. The chip palette itself (`SLOT_STATE_META`) is reused from
 * `LeaseStatusSection.tsx`'s `STATE_META`/`UNKNOWN_META` by way of
 * `slots-display.ts` — see that file's header for why the reuse is one layer
 * removed (the `react-refresh/only-export-components` lint gate).
 *
 * READ-ONLY. This page has no cancel, restart or drain control and no button
 * that would trigger one (plan exclusion #7) — every interactive element here
 * either links to a read-only detail page or does nothing but render.
 */
import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { UkkoSlotFinding, UkkoSlotQueue, UkkoSlotStatus, UkkoSlotWork } from '@a7y/types';
import { cn } from '@/lib/utils';
import { fmtDuration, fmtLocal, useNow } from './shared';
import { SLOT_STATE_META, slotTypeLabel } from './slots-display';
import { BannerRule } from './WorkloadsSections';

// ---------------------------------------------------------------------------
// MARK: state chip / type badge
// ---------------------------------------------------------------------------

export function SlotStateChip({ state }: { state: UkkoSlotStatus['state'] }) {
  const meta = SLOT_STATE_META[state];
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium',
        meta.chip
      )}
    >
      {meta.label}
    </span>
  );
}

export function SlotTypeBadge({ work }: { work: UkkoSlotWork | null }) {
  return (
    <span className="truncate font-mono text-[11px] text-zinc-600 dark:text-zinc-400">
      {slotTypeLabel(work)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// MARK: slot card / group
// ---------------------------------------------------------------------------

function ageFrom(iso: string, nowMs: number): string {
  const delta = nowMs - Date.parse(iso);
  if (!Number.isFinite(delta)) return '—';
  return `${fmtDuration(Math.max(0, delta) / 1000)} ago`;
}

function CardRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-xs">
      <span className="shrink-0 text-zinc-500">{label}</span>
      <span className="min-w-0 text-right text-zinc-800 dark:text-zinc-200">{children}</span>
    </div>
  );
}

/**
 * A session-detail link is only rendered once `sessionFamily` is also
 * present, since that (not the raw `sessionKind`) is the value the
 * `/admin/automation/:kind/:stamp` route needs — `sessionFamily` collapses
 * `cdid`, `cdid-worker-reconcile`, `addis-ingress-scheduler`, `cdid-lander`
 * and `cdid-hot-lane` onto the one family the session-detail page actually
 * understands (D5). The gate stated in the plan — `sessionKind` and
 * `sessionStamp` both present — is a subset of this, since `sessionFamily`
 * cannot resolve without a `sessionKind`.
 */
function SessionLink({ work }: { work: UkkoSlotWork }) {
  if (!work.sessionKind || !work.sessionStamp || !work.sessionFamily) return null;
  return (
    <Link
      to={`/admin/automation/${work.sessionFamily}/${encodeURIComponent(work.sessionStamp)}`}
      className="inline-flex items-center gap-1 text-blue-600 hover:underline dark:text-blue-400"
    >
      Session detail
      <ExternalLink className="h-3 w-3 shrink-0" />
    </Link>
  );
}

export function SlotCard({ slot, nowMs }: { slot: UkkoSlotStatus; nowMs: number }) {
  const meta = SLOT_STATE_META[slot.state];
  const work = slot.work;
  return (
    <div className={cn('rounded-lg border bg-white p-3 dark:bg-zinc-900', meta.card)}>
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-mono text-sm font-semibold text-zinc-900 dark:text-zinc-100">
            Slot {slot.index}
          </div>
          {work ? <SlotTypeBadge work={work} /> : null}
        </div>
        <SlotStateChip state={slot.state} />
      </div>

      {work ? (
        <div className="space-y-1.5">
          <CardRow label="Tickets">
            <span className="font-mono text-[11px]">
              {work.tickets.length > 0 ? work.tickets.join(', ') : work.displayTitle}
            </span>
          </CardRow>
          <CardRow label="Driver account">
            {work.driverAccount ?? <span className="text-zinc-400">—</span>}
          </CardRow>
          <CardRow label="Elapsed">
            <span title={fmtLocal(work.startedAt)}>{fmtDuration((nowMs - Date.parse(work.startedAt)) / 1000)}</span>
          </CardRow>
          <CardRow label="Last activity">
            {work.lastActivityAt ? (
              <span title={fmtLocal(work.lastActivityAt)}>{ageFrom(work.lastActivityAt, nowMs)}</span>
            ) : (
              <span className="text-zinc-400">not recorded</span>
            )}
          </CardRow>
          <div className="pt-1 text-right text-[11px]">
            <SessionLink work={work} />
          </div>
        </div>
      ) : null}

      <p className="mt-2 border-t border-zinc-100 pt-2 text-[11px] leading-relaxed text-zinc-500 dark:border-zinc-800 dark:text-zinc-400">
        {slot.reason}
      </p>
    </div>
  );
}

const GROUP_TITLES: Record<UkkoSlotStatus['slotClass'], string> = {
  session: 'Session',
  verify: 'Verify',
  deploy: 'Deploy',
};

export function SlotGroup({
  slotClass,
  slots,
  nowMs,
}: {
  slotClass: UkkoSlotStatus['slotClass'];
  slots: UkkoSlotStatus[];
  nowMs: number;
}) {
  return (
    <section className="space-y-2">
      <BannerRule>
        {GROUP_TITLES[slotClass]} ({slots.length})
      </BannerRule>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {slots.map((slot) => (
          <SlotCard key={slot.index} slot={slot} nowMs={nowMs} />
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// MARK: queue lines
// ---------------------------------------------------------------------------

export function QueueLines({ queues }: { queues: UkkoSlotQueue[] }) {
  const now = useNow(1_000);
  return (
    <section className="space-y-2 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
      <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Queues</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {queues.map((queue) => (
          <div
            key={queue.label}
            className="rounded-lg border border-zinc-200 p-2.5 dark:border-zinc-800"
          >
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <span className="font-mono text-xs text-zinc-700 dark:text-zinc-300">
                {queue.label}
              </span>
              <span className="font-mono text-xs tabular-nums text-zinc-500">
                {queue.queuedCount}
              </span>
            </div>
            {queue.starved.length === 0 ? (
              <p className="text-[11px] text-zinc-400">No starved jobs.</p>
            ) : (
              <ul className="space-y-1">
                {queue.starved.map((entry) => (
                  <li key={entry.jobId} className="text-[11px] text-amber-700 dark:text-amber-400">
                    <a
                      href={entry.runUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 hover:underline"
                    >
                      <span className="truncate">{entry.jobName || entry.displayTitle}</span>
                      <ExternalLink className="h-3 w-3 shrink-0" />
                    </a>
                    <span className="ml-1 text-zinc-500">— {ageFrom(entry.queuedAt, now)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
        {queues.length === 0 && (
          <p className="text-xs text-zinc-400">No queue data reported.</p>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// MARK: findings
// ---------------------------------------------------------------------------

const FINDING_LABELS: Record<UkkoSlotFinding['code'], string> = {
  'unlisted-slot': 'Unlisted slot',
  'slot-double-claim': 'Double claim',
};

export function SlotsFindings({ findings }: { findings: UkkoSlotFinding[] }) {
  if (findings.length === 0) {
    return (
      <section className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
        <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Findings</h2>
        <p className="mt-2 text-xs text-zinc-500">No findings.</p>
      </section>
    );
  }

  return (
    <section className="space-y-2 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
      <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Findings</h2>
      <ul className="space-y-1.5">
        {findings.map((finding, index) => (
          <li
            key={`${finding.code}-${finding.slotIndex ?? 'none'}-${index}`}
            className="text-xs text-zinc-700 dark:text-zinc-300"
          >
            <span className="font-medium">{FINDING_LABELS[finding.code]}</span>
            {finding.slotIndex != null ? (
              <span className="ml-1 font-mono text-zinc-500">slot {finding.slotIndex}</span>
            ) : null}
            <span className="ml-1 text-zinc-500">— {finding.summary}</span>
            {finding.runIds.length > 0 ? (
              <span className="ml-1 font-mono text-[11px] text-zinc-400">
                ({finding.runIds.join(', ')})
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// MARK: legend + unreadable note
// ---------------------------------------------------------------------------

/**
 * Six sentences, each a declared limitation this page would otherwise hide
 * from a reader looking straight at it. Each maps to a numbered exclusion in
 * §5 of the plan — see the comment on each `<p>` below.
 */
export function SlotsLegend({ slotCount }: { slotCount: number }) {
  return (
    <section className="space-y-1.5 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4 text-[11px] text-zinc-500 dark:text-zinc-400">
      {/* §5.1 / D4 */}
      <p>
        <span className="font-semibold text-zinc-700 dark:text-zinc-300">Slot class is a capacity
        label.</span>{' '}
        <code className="font-mono">ukko</code> is additive on session and verify slots, so a verify
        slot routinely runs session-adjacent and CI work.
      </p>
      {/* §5.1 / D1, D2 */}
      <p>
        <span className="font-semibold text-zinc-700 dark:text-zinc-300">These are the {slotCount}{' '}
        configured slots.</span>{' '}
        This page has no runner-registration or offline signal — an absent job is the only idle
        evidence available.
      </p>
      {/* §5.2 / D7 */}
      <p>
        <span className="font-semibold text-zinc-700 dark:text-zinc-300">
          Only a job actually running a session workflow&apos;s <em>Run session</em> step carries a
          silence verdict.
        </span>{' '}
        Every other job — including a CDID admit job or a worker-reconcile job on the same slot —
        shows type and elapsed but is never called stuck. <code className="font-mono">stuck</code>{' '}
        asserts 30 minutes of silence, never a cause.
      </p>
      {/* §5.4 / D8 */}
      <p>
        <span className="font-semibold text-zinc-700 dark:text-zinc-300">The queue line counts
        jobs</span>{' '}
        GitHub reports as <code className="font-mono">queued</code>, including those inside a run
        already reporting <code className="font-mono">in_progress</code>. A run held by a
        concurrency group reports as <code className="font-mono">pending</code>, carries no jobs
        and therefore no label, and is not counted.
      </p>
      {/* §5.3 / D9 */}
      <p>
        <span className="font-semibold text-zinc-700 dark:text-zinc-300">Which dispatcher started a
        CDID session</span>{' '}
        — hot lane, ADGS, wave or manual — is not recorded anywhere this API can read, so no
        session is labelled hot-lane. The hot lane appears only when its own run occupies a slot.
      </p>
      {/* §5.5 / D6 (D17) */}
      <p>
        <span className="font-semibold text-zinc-700 dark:text-zinc-300">An overtime verdict is
        issued only where a declared budget exists:</span>{' '}
        the CDID orchestrator (90m), CDID worker (600m), AHC and APD (355m), and the ADDIS
        ingress scheduler (12m). Lander, hot-lane, AQUIX, state-invariants and manual runs carry
        no budget anywhere in the tree, so none is asserted here — and only for the job actually
        running that workflow&rsquo;s session step; an admit or reconcile job on the same run shows
        type and elapsed but no budget.
      </p>
    </section>
  );
}

export function UnreadableNote({ unreadable }: { unreadable: string[] }) {
  if (unreadable.length === 0) return null;
  return (
    <p className="text-xs text-amber-700 dark:text-amber-400">Could not read: {unreadable.join(', ')}</p>
  );
}
